import { createContext, useContext, useId, useMemo } from 'react';
import type { ButtonHTMLAttributes, JSX, KeyboardEvent, ReactNode } from 'react';
import { FOCUS_RING, cx } from './styles';

/*
 * 页签。**只做内联渲染的版本**：不 Portal、不做浮层定位 ——
 * 浮层与定位属于浮层系统（overlay）的职责，这里碰了就会和它抢同一块地方。
 *
 * 键盘约定（WAI-ARIA Tabs 模式）：
 *   ←/→（以及 ↑/↓）在页签之间移动，Home/End 到首尾，循环（到尾部再按一下回到头部）。
 *   焦点移动**即选中**（自动激活）。这是有意的：页签内容都是本地已渲染的，
 *   切换零成本；将来若有"切过去就要重新请求"的重面板，必须改成手动激活
 *   （Enter/Space 才切换），否则用方向键翻一遍会打出一串请求。
 */

interface TabsContextValue {
  value: string;
  onChange: (value: string) => void;
  baseId: string;
}

const TabsContext = createContext<TabsContextValue | null>(null);

/* id 里不能有空格，否则 `aria-controls` 指向不了。允许的字符集收紧到 CSS/HTML 都无歧义的集合 */
function sanitizeFragment(value: string): string {
  return value.replace(/[^A-Za-z0-9_-]/g, '-');
}

export interface TabsProps {
  value: string;
  onChange: (value: string) => void;
  children: ReactNode;
  className?: string;
}

export interface TabListProps {
  children: ReactNode;
  'aria-label'?: string;
  className?: string;
}

export interface TabProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'className' | 'id' | 'onChange' | 'role' | 'type' | 'value'> {
  value: string;
  children: ReactNode;
  className?: string;
}

export interface TabPanelProps {
  value: string;
  children: ReactNode;
  /** 未选中时仍然留在 DOM 里（只是 `hidden`）：内容里有滚动位置/输入焦点时用得上 */
  keepMounted?: boolean;
  className?: string;
}

/** 页签容器。只提供状态与 id 前缀，不渲染任何可见的东西 */
export function Tabs({ value, onChange, children, className }: TabsProps): JSX.Element {
  const baseId = useId().replaceAll(':', '');
  const context = useMemo<TabsContextValue>(() => ({ value, onChange, baseId }), [value, onChange, baseId]);

  return (
    <TabsContext.Provider value={context}>
      <div className={cx('flex min-h-0 flex-col gap-2', className)}>{children}</div>
    </TabsContext.Provider>
  );
}

export function TabList({ children, 'aria-label': ariaLabel, className }: TabListProps): JSX.Element {
  const context = useContext(TabsContext);

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    const handled = ['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (!handled.includes(event.key)) return;

    /*
     * 按 **DOM 顺序**收集页签，而不是按 children 顺序：Tab 可能被包在别的元素里
     * （分组、条件渲染），只有 DOM 顺序等于用户看到的顺序。
     * 禁用的页签不参与导航 —— 它们本来就拿不到焦点。
     */
    const tabs = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]')).filter(
      (tab) => !tab.disabled,
    );
    if (tabs.length === 0) return;

    const last = tabs.length - 1;
    const currentIndex = tabs.findIndex((tab) => tab === document.activeElement);

    let nextIndex: number;
    if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = last;
    } else {
      const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
      // 焦点不在页签上时（例如脚本触发了 keydown），从"已选中的那个"出发，别从头开始
      const selectedIndex = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      const from = currentIndex >= 0 ? currentIndex : Math.max(0, selectedIndex);
      nextIndex = from + step;
      if (nextIndex > last) nextIndex = 0;
      if (nextIndex < 0) nextIndex = last;
    }

    const target = tabs[nextIndex];
    const value = target?.dataset.tabValue;
    if (target === undefined || value === undefined) return;

    // 拦下默认行为：否则方向键会同时滚动页面
    event.preventDefault();
    target.focus();
    context?.onChange(value);
  }

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
      className={cx('flex flex-wrap items-center gap-1 border-b border-line', className)}
    >
      {children}
    </div>
  );
}

export function Tab({ value, children, className, disabled = false, ...rest }: TabProps): JSX.Element {
  const context = useContext(TabsContext);
  if (context === null) {
    // 脱离 Tabs 使用是接线错误。安静地不渲染，比渲染一个点了没反应、aria 全错的页签好
    return <></>;
  }

  const selected = context.value === value;
  const fragment = sanitizeFragment(value);

  return (
    <button
      {...rest}
      type="button"
      role="tab"
      id={`${context.baseId}-tab-${fragment}`}
      aria-selected={selected}
      aria-controls={`${context.baseId}-panel-${fragment}`}
      // 只让选中的页签进 Tab 序列：否则"从页签跳到内容"要按 N 次 Tab
      tabIndex={selected ? 0 : -1}
      data-tab-value={value}
      disabled={disabled}
      onClick={() => context.onChange(value)}
      className={cx(
        '-mb-px border-b-2 px-2 py-1 text-xs disabled:cursor-not-allowed disabled:opacity-50',
        selected ? 'border-accent text-fg' : 'border-transparent text-fg-muted hover:text-fg',
        FOCUS_RING,
        className,
      )}
    >
      {children}
    </button>
  );
}

export function TabPanel({ value, children, keepMounted = false, className }: TabPanelProps): JSX.Element | null {
  const context = useContext(TabsContext);
  if (context === null) return null;

  const selected = context.value === value;
  if (!selected && !keepMounted) return null;

  const fragment = sanitizeFragment(value);

  return (
    <div
      role="tabpanel"
      id={`${context.baseId}-panel-${fragment}`}
      aria-labelledby={`${context.baseId}-tab-${fragment}`}
      hidden={!selected}
      // 面板本身可聚焦（`tabIndex=0`）：面板里若没有可聚焦元素，键盘用户按 Tab 会直接跳过整块内容
      tabIndex={0}
      className={cx('min-h-0 flex-1 text-sm text-fg', FOCUS_RING, className)}
    >
      {children}
    </div>
  );
}
